import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { staffApi, businessApi } from '../services/api';
import { StaffMember, Business } from '../types';
import { Modal } from '../components/Modal';

const staffSchema = z.object({
  businessId: z.string().min(1, 'Business is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email'),
  phone: z.string().optional(),
  role: z.string().min(1, 'Role is required'),
  bio: z.string().optional(),
  specialties: z.string().optional(),
});

type StaffForm = z.infer<typeof staffSchema>;

export function Staff() {
  const [searchParams] = useSearchParams();
  const initialBusinessId = searchParams.get('businessId') || '';
  
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<StaffForm>({
    resolver: zodResolver(staffSchema),
    defaultValues: {
      businessId: initialBusinessId,
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const businessesRes = await businessApi.getMyBusinesses();
      const businesses = businessesRes.data.data.businesses || [];
      setBusinesses(businesses);

      if (businesses.length > 0) {
        const staffRes = await staffApi.getBusinessStaff(businesses[0].id);
        setStaff(staffRes.data.data.staff || []);
      } else {
        setStaff([]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: StaffForm) => {
    try {
      const specialties = data.specialties
        ? data.specialties.split(',').map((s) => s.trim()).filter(Boolean)
        : [];

      const submitData = { ...data, specialties };

      if (editingStaff) {
        await staffApi.updateStaff(editingStaff.id, submitData);
        toast.success('Staff member updated successfully');
      } else {
        await staffApi.createStaff(submitData);
        toast.success('Staff member created successfully');
      }
      setIsModalOpen(false);
      setEditingStaff(null);
      reset();
      fetchData();
    } catch (error) {
      toast.error(editingStaff ? 'Failed to update staff' : 'Failed to create staff');
    }
  };

  const handleEdit = (member: StaffMember) => {
    setEditingStaff(member);
    reset({
      businessId: member.businessId,
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      phone: member.phone || '',
      role: member.role,
      bio: member.bio || '',
      specialties: member.specialties?.join(', ') || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this staff member?')) return;
    
    try {
      await staffApi.deleteStaff(id);
      toast.success('Staff member deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete staff member');
    }
  };

  const handleAddNew = () => {
    setEditingStaff(null);
    reset({
      businessId: businesses[0]?.id || '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: '',
      bio: '',
      specialties: '',
    });
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-secondary-900">Staff Members</h1>
        <button onClick={handleAddNew} className="btn-primary">
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Staff
        </button>
      </div>

      {staff.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staff.map((member) => (
            <div key={member.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-lg font-medium text-primary-700">
                      {member.firstName[0]}{member.lastName[0]}
                    </span>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-secondary-900">
                      {member.firstName} {member.lastName}
                    </h3>
                    <p className="text-sm text-secondary-500">{member.role}</p>
                  </div>
                </div>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    member.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {member.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="mt-4 space-y-2 text-sm text-secondary-600">
                <p>{member.email}</p>
                {member.phone && <p>{member.phone}</p>}
              </div>

              {member.specialties && member.specialties.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-secondary-500 mb-2">Specialties:</p>
                  <div className="flex flex-wrap gap-1">
                    {member.specialties.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex space-x-2">
                <button
                  onClick={() => handleEdit(member)}
                  className="flex-1 btn-secondary text-sm"
                >
                  <PencilIcon className="h-4 w-4 mr-1 inline" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(member.id)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-secondary-500 mb-4">No staff members yet.</p>
          <button onClick={handleAddNew} className="btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Your First Staff Member
          </button>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingStaff(null);
        }}
        title={editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!editingStaff && (
            <div>
              <label className="label">Business *</label>
              <select {...register('businessId')} className="input">
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {errors.businessId && (
                <p className="text-sm text-red-600 mt-1">{errors.businessId.message}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">First Name *</label>
              <input {...register('firstName')} type="text" className="input" />
              {errors.firstName && (
                <p className="text-sm text-red-600 mt-1">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="label">Last Name *</label>
              <input {...register('lastName')} type="text" className="input" />
              {errors.lastName && (
                <p className="text-sm text-red-600 mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="label">Email *</label>
            <input {...register('email')} type="email" className="input" />
            {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Phone</label>
              <input {...register('phone')} type="tel" className="input" />
            </div>
            <div>
              <label className="label">Role *</label>
              <input {...register('role')} type="text" className="input" placeholder="e.g. Trainer" />
              {errors.role && <p className="text-sm text-red-600 mt-1">{errors.role.message}</p>}
            </div>
          </div>

          <div>
            <label className="label">Bio</label>
            <textarea {...register('bio')} className="input" rows={2} />
          </div>

          <div>
            <label className="label">Specialties (comma separated)</label>
            <input
              {...register('specialties')}
              type="text"
              className="input"
              placeholder="e.g. Yoga, Pilates, Cardio"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setEditingStaff(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Saving...' : editingStaff ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
